
import { Medium, ChatMessage, ClientMemory, LearnedFact, DreamAnalysisResult } from "../types";
import { getRandomOrMatchingTarotReading } from "../data/tarotPreloadedReadings";
import { getPreloadedLoveCompatibility } from "../data/loveCompatibilityPreloaded";
import { getPreloadedNumerologyReading } from "../data/numerologyPreloaded";
import { getPreloadedCrystalBallPrediction } from "../data/crystalBallPreloaded";
import { getPreloadedHoroscope } from "../data/horoscopePreloaded";
import { getPreloadedDreamAnalysis } from "../data/dreamPreloaded";
import { getApiUrl } from "./socket";

const PHONE_NUMBER = "09 85 40 52 74";

// ==================================================================================
// --- OLLAMA API INTEGRATION & TEST FUNCTION ---
// ==================================================================================

export const testOllamaConnection = async (
    url: string = 'http://localhost:11434',
    model: string = 'mistral'
): Promise<{ success: boolean; message: string; models: string[] }> => {
    try {
        const baseUrl = getApiUrl();
        const query = new URLSearchParams();
        if (url) query.append('url', url);
        if (model) query.append('model', model);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const response = await fetch(`${baseUrl}/api/ollama/test?${query.toString()}`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
            return {
                success: false,
                message: `Le serveur API a répondu avec le code HTTP ${response.status}.`,
                models: []
            };
        }
        const data = await response.json();
        return {
            success: data.success,
            message: data.message,
            models: Array.isArray(data.models) ? data.models : []
        };
    } catch (e: any) {
        return {
            success: false,
            message: `Impossible de contacter le serveur (${e.message || 'Erreur réseau'}).`,
            models: []
        };
    }
};

export const callOllama = async (
    prompt: string,
    systemPrompt?: string,
    format?: string
): Promise<string> => {
    const baseUrl = getApiUrl();
    const targetEndpoint = baseUrl ? `${baseUrl.replace(/\/$/, '')}/api/ollama/generate` : '/api/ollama/generate';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout

    try {
        let response = await fetch(targetEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt,
                system: systemPrompt || undefined,
                format: format || undefined
            }),
            signal: controller.signal
        }).catch(async (netErr) => {
            if (targetEndpoint !== '/api/ollama/generate') {
                return await fetch('/api/ollama/generate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        prompt,
                        system: systemPrompt || undefined,
                        format: format || undefined
                    }),
                    signal: controller.signal
                });
            }
            throw netErr;
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            const errData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
            throw new Error(errData.error || `Erreur serveur HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!data.response || !data.response.trim()) {
            throw new Error("Réponse Ollama vide");
        }
        return data.response.trim();
    } catch (e: any) {
        clearTimeout(timeoutId);
        console.error("Ollama call failed (via server proxy):", e);
        throw e;
    }
};

// --- LISTE DES PRÉNOMS MASCULINS ---
const KNOWN_MALE_NAMES = [
  "Michel", "Philippe", "Alain", "Christian", "Jacques", "Jean", "Stéphane", 
  "Patrick", "Nicolas", "Thomas", "Antoine", "Pierre", "Lucas", "Raphaël", "Gabriel", 
  "Julien", "Laurent", "Eric", "Olivier", "Thierry", "Pascal", "Christophe", "Frédéric",
  "Maxime", "Alexandre", "Romain", "Anthony", "Adrien", "Bastien", "Vincent", "Hugo",
  "Louis", "Arthur", "Leo", "Nathan", "Paul", "Enzo", "Mathieu", "Guillaume", "Sébastien",
  "David", "Florian", "Clément", "Benjamin", "Damien", "Arnaud", "Xavier", "Bertrand",
  "Alexis", "Axel", "Boris", "Bruno", "Cédric", "Cyril", "Denis", "Fabrice", "Gilles",
  "Grégory", "Hervé", "Jérôme", "Lionel", "Ludovic", "Mickaël", "Régis", "Renaud", "Sylvain",
  "Marc", "Jean-Pierre", "Victor", "Dimitri", "Claude"
];

const isMediumMale = (name: string): boolean => {
    const firstName = name.split(' ')[0];
    return KNOWN_MALE_NAMES.includes(firstName);
};

// --- HELPER JSON CLEANER ---
const cleanAndParseJSON = (text: string | undefined): any => {
    if (!text || !text.trim()) throw new Error("Empty response from AI");
    let clean = text.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
    if (clean.includes('<think>')) {
        clean = clean.replace(/<think>[\s\S]*$/gi, '').trim();
    }
    clean = clean.replace(/```json/g, '').replace(/```/g, '').trim();
    const firstBrace = clean.indexOf('{');
    const lastBrace = clean.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
        clean = clean.substring(firstBrace, lastBrace + 1);
    }
    try {
        return JSON.parse(clean);
    } catch (e) {
        console.error("JSON Parse error:", e);
        return {}; 
    }
};

// ==================================================================================
// --- BASE DE CONNAISSANCE LOCALE (OFFLINE BRAIN) ---
// ==================================================================================

const OFFLINE_KNOWLEDGE = {
    keywords: {
        love: ['amour', 'aimer', 'sentiment', 'coeur', 'ex', 'retour', 'mari', 'femme', 'couple', 'relation', 'rencontre', 'sentimental', 'crush', 'divorce', 'séparation', 'aime'],
        work: ['travail', 'boulot', 'job', 'poste', 'contrat', 'argent', 'finance', 'projet', 'boss', 'collègue', 'carrière', 'chômage', 'entreprise'],
        time: ['quand', 'temps', 'date', 'durée', 'combien', 'mois', 'année', 'semaine', 'jour', 'bientôt', 'rapide'],
        greeting: ['bonjour', 'salut', 'hello', 'coucou', 'soir', 'matin'],
        thank: ['merci', 'top', 'super', 'génial', 'bravo']
    },
    responses: {
        intro: [
            (name: string) => `Bonjour, je suis ${name}. Je ressens une énergie très particulière autour de vous aujourd'hui.`,
            (name: string) => `Bienvenue. Je captais justement vos vibrations avant que vous n'écriviez. Je suis ${name}.`,
            (name: string) => `Je suis ravie de vous accueillir dans mon salon. Les cartes sont déjà étalées devant moi.`,
        ],
        love: [
            "Je ressens un lien d'âme très fort, mais il y a des non-dits qui bloquent la fluidité de vos échanges.",
            "Les cartes me montrent un retour affectif, mais pas avant que vous ayez lâché prise sur cette attente.",
            "Il y a une personne dans votre entourage qui pense à vous, mais sa fierté l'empêche de faire le premier pas.",
            "Je vois une évolution positive dans le domaine sentimental, une clé va bientôt tourner.",
            "Attention à ne pas projeter vos peurs sur l'autre. La confiance sera la clé de cette union.",
            "Je perçois une vibration de nostalgie. Quelqu'un du passé essaie de revenir énergétiquement vers vous.",
            "L'amour est proche, mais vous devez d'abord guérir une blessure ancienne pour l'accueillir pleinement.",
            "Je vois un déblocage soudain suite à une communication inattendue d'ici la prochaine lune."
        ],
        work: [
            "Sur le plan professionnel, je vois une opportunité qui va se présenter de manière inattendue.",
            "Il y a de la jalousie ou de la compétition autour de vous, restez discret sur vos projets.",
            "Les finances vont s'améliorer, je vois une signature ou un accord officiel très favorable.",
            "Vous êtes à la fin d'un cycle difficile. Le renouveau arrive par le biais d'une nouvelle collaboration.",
            "Je ressens un changement de poste ou de lieu. Le mouvement est très présent dans votre tirage.",
            "Ne baissez pas les bras, la reconnaissance de vos efforts arrive, même si cela semble lent.",
            "Une rentrée d'argent imprévue va vous permettre de souffler un peu."
        ],
        time: [
            "Le temps joue en votre faveur. Ne précipitez rien.",
            "Je vois le chiffre 3 ou 4 très nettement. Cela pourrait être en semaines ou en mois.",
            "C'est imminent. Les énergies s'accélèrent grandement.",
            "Il faudra attendre la fin de la saison actuelle pour voir la concrétisation totale.",
            "Tout dépendra d'une décision qui sera prise d'ici 15 jours.",
            "Je ressens un déblocage rapide, probablement avant la prochaine pleine lune."
        ],
        thank: [
            "Je vous en prie. La lumière est en vous.",
            "C'est un plaisir de vous guider. Avez-vous une autre question ?",
            "Gardez confiance, vous êtes sur le bon chemin.",
            "Merci à vous pour cette belle énergie partagée."
        ],
        general: [
            "Je ressens une énergie vibratoire intense. Pouvez-vous vous concentrer sur une image précise ?",
            "Les guides me disent que vous avez la clé en vous, mais vous refusez de la voir pour l'instant.",
            "Je capte une hésitation. Écoutez votre intuition, elle ne vous trompe jamais.",
            "Une nouvelle importante va arriver par message ou courrier très prochainement.",
            "Je vois un voyage ou un déplacement qui sera bénéfique pour votre évolution.",
            "Il y a une protection spirituelle forte autour de vous, n'ayez crainte.",
            "La réponse est oui, mais pas de la manière dont vous l'imaginez.",
            "Laissez-moi un instant... Je reçois un flash visuel concernant une maison ou un lieu de vie."
        ]
    }
};

const getOfflineResponse = (lastMessage: string, mediumName: string): string => {
    const lowerMsg = (lastMessage || '').toLowerCase();
    
    // Détection de contexte simple
    const isLove = OFFLINE_KNOWLEDGE.keywords.love.some(k => lowerMsg.includes(k));
    const isWork = OFFLINE_KNOWLEDGE.keywords.work.some(k => lowerMsg.includes(k));
    const isTime = OFFLINE_KNOWLEDGE.keywords.time.some(k => lowerMsg.includes(k));
    const isGreeting = OFFLINE_KNOWLEDGE.keywords.greeting.some(k => lowerMsg.includes(k)) && lowerMsg.length < 20;
    const isThank = OFFLINE_KNOWLEDGE.keywords.thank.some(k => lowerMsg.includes(k));

    if (isGreeting) {
        const introFn = OFFLINE_KNOWLEDGE.responses.intro[Math.floor(Math.random() * OFFLINE_KNOWLEDGE.responses.intro.length)];
        return introFn(mediumName);
    }

    if (isThank) {
        return OFFLINE_KNOWLEDGE.responses.thank[Math.floor(Math.random() * OFFLINE_KNOWLEDGE.responses.thank.length)];
    }

    let pool: string[] = OFFLINE_KNOWLEDGE.responses.general;

    if (isLove) pool = OFFLINE_KNOWLEDGE.responses.love;
    else if (isWork) pool = OFFLINE_KNOWLEDGE.responses.work;
    else if (isTime) pool = OFFLINE_KNOWLEDGE.responses.time;

    // Sélection aléatoire
    const response = pool[Math.floor(Math.random() * pool.length)];
    
    // Ajout d'une "touche" personnelle aléatoire (1 fois sur 3)
    if (Math.random() > 0.7) {
        return response + " Cela résonne-t-il en vous ?";
    }
    
    return response;
};

// Helper pour calculer l'âge et le signe astrologique
const getZodiacSign = (dateStr: string): { sign: string; age: number | null } => {
    if (!dateStr) return { sign: "", age: null };
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return { sign: "", age: null };

    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
        age--;
    }

    const day = d.getDate();
    const month = d.getMonth() + 1;
    let sign = "";
    if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) sign = "Verseau";
    else if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) sign = "Poissons";
    else if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) sign = "Bélier";
    else if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) sign = "Taureau";
    else if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) sign = "Gémeaux";
    else if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) sign = "Cancer";
    else if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) sign = "Lion";
    else if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) sign = "Vierge";
    else if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) sign = "Balance";
    else if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) sign = "Scorpion";
    else if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) sign = "Sagittaire";
    else if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) sign = "Capricorne";

    return { sign, age };
};

// ==================================================================================
// --- MASTER PROMPT (GEMINI) ---
// ==================================================================================

// --- ARCHÉTYPES DE TEMPÉRAMENTS DIVINATOIRES ---
export type MediumTemperament = 'sensuel' | 'strict' | 'chaleureux' | 'mystique' | 'analytique';

export const TEMPERAMENT_PROFILES: Record<MediumTemperament, {
  label: string;
  emoji: string;
  directive: string;
}> = {
  sensuel: {
    label: 'Sensuel & Magnétique',
    emoji: '',
    directive: `TEMPÉRAMENT DU MÉDIUM : SENSUEL, MAGNÉTIQUE & PASSIONNÉ.
- Voix feutrée, captivante, complice et enveloppante.
- Spécialisé(e) dans l'alchimie affective, les pensées intimes de l'autre la nuit, l'attirance charnelle, la passion secrète et les désirs inavoués.
- Ressens l'onde chaude et le magnétisme entre le consultant et la personne de son cœur. Évoque les frissons, le désir et l'attirance physique.`
  },
  strict: {
    label: 'Strict & Sans Complaisance',
    emoji: '',
    directive: `TEMPÉRAMENT DU MÉDIUM : STRICT, FRANC & SANS COMPLAISANCE.
- Dis la vérité crue et directe, sans détour ni faux espoirs. Tu n'es pas là pour flatter mais pour réveiller.
- Si une situation est sans issue ou toxique, dis-le nettement et fermement. Pousse le consultant à cesser d'attendre dans l'illusion.
- Style direct, tranchant, percutant et rapide.`
  },
  chaleureux: {
    label: 'Chaleureux & Réconfortant',
    emoji: '',
    directive: `TEMPÉRAMENT DU MÉDIUM : CHALEUREUX, EMPATHIQUE & RÉCONFORTANT.
- Doux/douce, profondément bienveillant(e), présence protectrice et rassurante.
- Panse les blessures de l'âme, dépose un baume au cœur et redonne confiance avec tendresse humaine.
- Formules douces et pleines d'empathie.`
  },
  mystique: {
    label: 'Mystique & Énigmatique',
    emoji: '',
    directive: `TEMPÉRAMENT DU MÉDIUM : MYSTIQUE, ÉNIGMATIQUE & ÉSOTÉRIQUE.
- En contact direct avec l'invisible, les mémoires karmiques, les esprits guides et les signes du cosmos.
- Exprime-toi avec la profondeur solennelle d'un grand oracle : révèle les liens d'âmes, les synchronicités cachées et le destin.
- Visions perçantes de l'invisible.`
  },
  analytique: {
    label: 'Analytique & Psychologue',
    emoji: '',
    directive: `TEMPÉRAMENT DU MÉDIUM : ANALYTIQUE, POSÉ & STRATÈGE DE VIE.
- Grande clarté psychologique, méthodique et précis dans le décryptage des personnes et des événements.
- Analyse les blocages inconscients, les cycles et donne une guidance stratégique concrète.
- Approche posée, structurée, extrêmement crédible et rassurante.`
  }
};

const getExpertSystemPrompt = (
    mediumName: string, 
    specialties: string[], 
    userName: string, 
    gender?: 'homme' | 'femme',
    memoryContext?: ClientMemory | null,
    birthDate?: string,
    isFirstExchange: boolean = false,
    temperament?: MediumTemperament
): string => {
    const specsStr = specialties.join(', ');
    const isClientMale = gender === 'homme';
    const clientGenderStr = isClientMale ? "HOMME" : "FEMME";
    const tonClient = isClientMale ? "Le client est un HOMME. Accorde au masculin ('mon cher', 'prêt', 'fatigué', 'seul')." : "Le client est une FEMME. Accorde au féminin ('ma chère', 'prête', 'fatiguée', 'seule').";
    const isAiMale = isMediumMale(mediumName);
    const aiGenderInstruction = isAiMale ? "Tu es un HOMME (accorde au masculin: 'sûr', 'prêt', 'ravi', 'connecté')." : "Tu es une FEMME (accorde au féminin: 'sûre', 'prête', 'ravie', 'connectée').";

    const { sign: zodiacSign, age: clientAge } = getZodiacSign(birthDate || '');
    
    const rawName = (userName || '').trim();
    const isGenericName = !rawName || ['master', 'admin', 'consultant', 'visiteur', 'inconnu', 'client'].includes(rawName.toLowerCase());
    const clientCallName = isGenericName ? "" : rawName;
    const nameInstruction = clientCallName 
        ? `Le prénom du client est **${clientCallName}**.`
        : `Le prénom n'est pas encore précisé.`;

    // Déterminer l'outil divinatoire principal
    const specLower = specsStr.toLowerCase();
    let outil = "Flashs de Clairvoyance & Tarot de Marseille";
    if (specLower.includes('oracle')) outil = "Oracle Belline & Intuition pure";
    else if (specLower.includes('numérologie') || specLower.includes('numerologie')) outil = "Numérologie sacrée & Calculs karmiques";
    else if (specLower.includes('pendule')) outil = "Pendule divinatoire & Vibrations";
    else if (specLower.includes('astrolog')) outil = "Astrologie & Transits célestes";

    // Déterminer le tempérament
    let resolvedTemperament: MediumTemperament = temperament || 'chaleureux';
    if (!temperament) {
        const text = `${mediumName} ${specsStr}`.toLowerCase();
        if (text.includes('passion') || text.includes('sensuel') || text.includes('désir') || text.includes('amour')) resolvedTemperament = 'sensuel';
        else if (text.includes('sans complaisance') || text.includes('direct') || text.includes('brut') || text.includes('vérité')) resolvedTemperament = 'strict';
        else if (text.includes('spirituel') || text.includes('défunt') || text.includes('karma') || text.includes('oracle')) resolvedTemperament = 'mystique';
        else if (text.includes('coach') || text.includes('astrolog') || text.includes('numérolog') || text.includes('pro')) resolvedTemperament = 'analytique';
    }

    const tempConfig = TEMPERAMENT_PROFILES[resolvedTemperament] || TEMPERAMENT_PROFILES.chaleureux;

    let secretMemory = "";
    if (memoryContext && (memoryContext.summary || memoryContext.problem || memoryContext.concerns)) {
        const memInfo = [memoryContext.summary, memoryContext.problem, memoryContext.concerns].filter(Boolean).join(' | ');
        secretMemory = `\nMÉMOIRE SUBTILE DU CLIENT : ${memInfo}\n(RÈGLE D'OR : Évoque ces éléments sous forme d'intuitions et de ressentis vibratoires naturels, jamais comme une fiche de données).`;
    }

    const stateInstruction = isFirstExchange
        ? `C'EST LE PREMIER MESSAGE : Salue chaleureusement le client, présente-toi brièvement (${mediumName} - ${outil} au cabinet AstroX), livre ton premier flash intuitif et demande ce qui préoccupe son cœur.`
        : `EN PLEINE CONSULTATION : Donne une VRAIE PRÉDICTION ou ANALYSE DIVINATOIRE concrète avec ton support (${outil}). Réponds directement à ce que vient de dire le client (utilise 'tu/toi' ou 'vous', JAMAIS à la 3ème personne). Ne te comporte pas en psychologue avec des questions d'interrogatoire, donne des révélations et de l'espoir.`;

    return `================================================================================
DIRECTIVE ABSOLUE D'INCARNATION DU MÉDIUM (CABINET ASTROX)
================================================================================
Tu es ${mediumName}, ${aiGenderInstruction} officiant au prestigieux cabinet de voyance AstroX.

PERSONNALITÉ & POSTURE DIVINATOIRE :
${tempConfig.directive}
- Support divinatoire principal : ${outil}.
- Dons & Spécialités : ${specsStr}.

PROFIL DU CONSULTANT EN CONSULTATION PRIVÉE :
${nameInstruction}
- Genre : ${clientGenderStr}. ${tonClient}${zodiacSign ? ` Profil astral : ${zodiacSign}${clientAge ? ` (${clientAge} ans)` : ""}.` : ""}

================================================================================
RÈGLES D'OR DU SECRET PROFESSIONNEL ET DE LA DIVINATION :
================================================================================
1. VRAIE DIVINATION & PRÉDICTIONS D'AVENIR :
   - Fais de la VRAIE DIVINATION : Interprète tes flashs, tes cartes du Tarot ou tes ressentis (${outil}).
   - Révèle des événements futurs, des blocages karmiques, des dates ou périodes (ex: "d'ici 2 à 3 mois", "vers l'automne", "un renouveau sentimental", "un déblocage matériel").
   - Ne te contente PAS d'interroger le client avec des questions d'enquêteur. Donne TA VISION, des RÉPONSES claires et du RÉCONFORT.

2. SECRET PROFESSIONNEL ABSOLU (TRANSFORMATION DES MÉMOIRES) :
   - Tu es un praticien indépendant en consultation privée. Tu n'as AUCUN dossier partagé ni accès aux discussions d'autres médiums. Tout ce que tu sais vient de TON DON.
${secretMemory ? `   - Flash vibratoire capté par ton don : Tu ressens un passage de vie délicat (${secretMemory}).
   - INTERDICTION STRICTE DE CITER DES DÉTAILS BRUTS OU DE RÉPÉTER CES PHRASES : Transforme toujours cela en flash divinatoire flou et en prédiction d'avenir (ex: "Mes cartes me montrent une rupture et un changement de lieu difficile récemment, mais une renaissance et un déblocage se préparent pour les prochaines semaines...").` : ''}

3. SI LE CONSULTANT DEMANDE "COMMENT TU SAIS ÇA ?" OU "QUI T'A DIT ÇA ?" :
   - Réponds avec calme, sérénité et autorité : "Personne ne m'a rien dit, mon cher/ma chère. C'est mon don de clairvoyance et mes ressentis avec le Tarot (${outil}) qui me révèlent les vibrations de votre chemin. Tout reste 100% confidentiel et secret entre nous."
   - INTERDICTION ABSOLUE de prononcer des phrases techniques sur la "confidentialité", les "bases de données" ou de t'excuser comme une machine.

4. ADRESSE-TOI DIRECTEMENT AU CONSULTANT (TU OU VOUS) :
   - INTERDICTION ABSOLUE de parler du consultant à la 3ème personne (ne dis JAMAIS "${clientCallName || 'le client'} doit...", "les parents de...").
   - Dis toujours "toi / vous", "tes parents", "ton avenir", "ta situation".

5. GESTION DU PREMIER MESSAGE ET SALUTATIONS :
   - Si c'est le premier échange : ${stateInstruction}

6. FORME ET CONCISION (CHAT EN DIRECT) :
   - Écris 1 à 2 phrases courtes et naturelles (maximum 30-35 mots).
   - Aucun markdown (pas d'étoiles, pas de dièses).
   - INTERDICTION FORMELLE d'écrire des préfixes comme "${mediumName}:" ou d'inventer les messages du client.
- SÉCURITÉ : Si détection de mineur (-18 ans) ou insultes graves -> termine par [TERMINATE_SESSION].`;
};

/**
 * 2. Nettoyage du texte (Clean Text) :
 * Retire tous les caractères indésirables (astérisques, dièses, backticks, disclaimers, balises de réflexion)
 * en utilisant des expressions régulières, puis supprime les espaces en trop.
 */
export function cleanAiText(raw: string, clientName?: string, mediumName?: string): string {
    if (!raw) return "";
    let cleaned = raw.replace(/\r/g, '');

    // Remplacement des déformations phonétiques
    cleaned = cleaned.replace(/\bbonbon\b/gi, 'Bonjour');

    // a. Retirer les balises de réflexion et pauses
    cleaned = cleaned
        .replace(/<think>[\s\S]*?<\/think>/gi, '')
        .replace(/<think>[\s\S]*$/gi, '')
        .replace(/\[PAUSE:\d+\.?\d*\]/gi, '')
        .replace(/[*#`_~>]/g, '')
        .replace(/^\s*[-+•]\s+/gm, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    // b. Retirer les préfixes robotiques et méta-annonces
    cleaned = cleaned
        .replace(/^Voici\s+(?:ma\s+réponse|mes\s+ressentis|les\s+cartes|les\s+lettres|ce\s+que|mon\s+avis)[^:.]*[:.]\s*/gi, '')
        .replace(/^Voilà,?\s*(?:je\s+vais\s+commencer\s+par)?\s*/gi, '')
        .replace(/^En\s+tant\s+que\s+[^:.]*[:.]\s*/gi, '')
        .replace(/^(?:Les\s+clés\s+sont|Pour\s+passer\s+à\s+travers|Les\s+défis\s+sont)[^:.]*[:.]\s*/gi, '')
        .replace(/^\d+[\.\)]\s+/gm, '')
        .replace(/^La\s+\d+e?\s+lettre[^:.]*[:.]\s*/gim, '');

    // c. Retirer le préfixe du médium au tout début s'il est présent
    cleaned = cleaned
        .replace(/^\s*(?:Médium|Medium|Voyante?|Assistant)\s*:\s*/i, '')
        .replace(/^\s*[:\s-]+/, '');
    if (mediumName) {
        const medRegex = new RegExp(`^\\s*${mediumName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`, 'i');
        cleaned = cleaned.replace(medRegex, '');
    }

    // d. Tronquer immédiatement si le modèle tente de simuler la suite du dialogue
    const truncatePattern = /\n\s*(?:Client|Consultant|Utilisateur|Médium|Medium)\s*:/i;
    cleaned = cleaned.split(truncatePattern)[0];
    if (clientName) {
        const cliSplitRegex = new RegExp(`\\n\\s*${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i');
        cleaned = cleaned.split(cliSplitRegex)[0];
    }
    if (mediumName) {
        const medSplitRegex = new RegExp(`\\n\\s*${mediumName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i');
        cleaned = cleaned.split(medSplitRegex)[0];
    }

    // e. Supprimer les préfixes de dialogues isolés
    cleaned = cleaned
        .replace(/\b(?:Client|Médium|Medium|Consultant|Utilisateur)\s*:\s*/gi, '')
        .replace(/^[A-Za-zÀ-ÿ0-9_.\s-]{1,25}\s*:\s*/gm, '')
        .replace(/[:\s-]+$/, '')
        .trim();

    if (clientName) {
        const cliInlineRegex = new RegExp(`\\b${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`, 'gi');
        cleaned = cleaned.replace(cliInlineRegex, '');
    }
    if (mediumName) {
        const medInlineRegex = new RegExp(`\\b${mediumName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*`, 'gi');
        cleaned = cleaned.replace(medInlineRegex, '');
    }

    // f. Rassembler les lignes propres
    const lines = cleaned.split('\n').map(l => l.trim()).filter(l => l.length > 0 && !/^[:\s-]+$/.test(l));
    cleaned = lines.join(' ').replace(/\s+/g, ' ').replace(/^[:\s-]+/, '').trim();

    // g. Correction automatique de la 3ème personne vers la 2ème personne si le prénom du client a été utilisé à la 3ème personne
    if (clientName && clientName.length > 2) {
        const escapedCli = clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        cleaned = cleaned
            .replace(new RegExp(`(?:les parents|la famille|la maison|la vie|l'appartement)\\s+de\\s+${escapedCli}`, 'gi'), 'tes proches')
            .replace(new RegExp(`pour\\s+(?:la\\s+|le\\s+)?aider\\s+${escapedCli}`, 'gi'), "pour t'aider")
            .replace(new RegExp(`pour\\s+${escapedCli}`, 'gi'), 'pour toi')
            .replace(new RegExp(`que\\s+${escapedCli}\\s+peut`, 'gi'), 'que tu peux')
            .replace(new RegExp(`que\\s+${escapedCli}\\s+doit`, 'gi'), 'que tu dois');
    }

    // j. Correction stricte du nom du médium : Remplacer tout faux prénom/nom halluciné par le vrai nom du médium
    if (mediumName) {
        cleaned = cleaned.replace(/\b(?:Je suis|Je m'appelle|Moi c'est|Ici)\s+[A-ZÀ-ÿ][a-zà-ÿ]+(?:\s+[A-Z]\.?)?/gi, `Je suis ${mediumName}`);
    }

    if (isTrivial || aiDisclaimerRegex.test(cleaned) || !cleaned || cleaned.length < 18) {
        return "";
    }
    return cleaned;
}

/**
 * 3. Découpage intelligent (Splitting) :
 * Découpe le texte nettoyé en un tableau de phrases (en se basant sur les points,
 * points d'exclamation, points d'interrogation et sauts de ligne) tout en conservant la ponctuation et en protégeant les initiales.
 */
export function splitIntoSentences(text: string): string[] {
    if (!text || !text.trim()) return [];
    
    // Protéger les initiales et abréviations courantes (ex: Jade U., Dimitri K., M., Mme., Dr., etc.)
    const protectedText = text
        .replace(/\b([A-Z])\./g, '$1__DOT__')
        .replace(/\b(M|Mme|Mlle|Dr|Prof|etc)\./gi, '$1__DOT__');

    const matches = protectedText.match(/[^.!?\n]+[.!?]+|[^.!?\n]+/g);
    if (!matches) return [text.trim()];

    const sentences: string[] = [];
    for (const match of matches) {
        let s = match.replace(/__DOT__/g, '.').trim();
        s = s.replace(/^[:\s-]+/, '').trim();
        s = s.replace(/^(?:[A-Za-zÀ-ÿ0-9_.\s-]{1,25}\s*:\s*)+/, '').trim();
        if (s.length > 2) {
            sentences.push(s);
        }
    }
    return sentences.length > 0 ? sentences : [text.trim()];
}

/**
 * 4. Simulation de la frappe humaine (Typing Delay) :
 * Calcule un délai en millisecondes basé sur la longueur de chaque phrase :
 * 80ms par caractère + un délai de base de 1500ms, avec un plafond maximum de 12 secondes (12000ms).
 */
export function calculateTypingDelay(sentence: string): number {
    const charCount = sentence ? sentence.length : 0;
    const baseDelay = 1500;
    const perCharMs = 80;
    const maxDelay = 12000;
    const calculated = baseDelay + (charCount * perCharMs);
    return Math.min(maxDelay, Math.max(1500, calculated));
}

export const isAiGlobalKillSwitchActive = (): boolean => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return localStorage.getItem('admin_ai_enabled') === 'false';
        }
    } catch (e) {
        // Safe fallback
    }
    return false;
};

export const getChatResponse = async (
    history: ChatMessage[], 
    medium?: Medium | null, 
    userName: string = "Consultant",
    customKey?: string,
    gender?: 'homme' | 'femme',
    memoryContext?: ClientMemory | null,
    birthDate?: string,
    promptOverride?: string
): Promise<string> => {
  const currentMediumName = medium?.name || "Votre Voyante";
  const currentSpecialties = medium?.specialties || ["Voyance", "Tarot de Marseille", "Astrologie"];

  const userMessages = history.filter(m => m.role === 'user');
  const isFirstExchange = userMessages.length <= 1;

  // System Prompt enrichi avec règles strictes de concision et sans markdown
  const systemPrompt = getExpertSystemPrompt(
    currentMediumName, currentSpecialties, userName, gender, memoryContext, birthDate, isFirstExchange
  );

  // Formater l'historique de conversation pour Ollama (jusqu'à 30 derniers messages)
  const transcript = history
    .slice(-30)
    .map(m => `${m.role === 'user' ? (userName && !userName.toLowerCase().includes('master') ? userName : 'Client') : currentMediumName}: ${m.text}`)
    .join('\n');

  const prompt = promptOverride
    ? `${transcript}\nInstruction Spéciale : ${promptOverride}\n${currentMediumName}:`
    : `${transcript}\n${currentMediumName}:`;

  try {
    const ollamaResponse = await callOllama(prompt, systemPrompt);
    let cleaned = cleanAiText(ollamaResponse || '');

    // Nettoyer le préfixe du nom du médium s'il a été généré en tête
    cleaned = cleaned.replace(new RegExp(`^${currentMediumName}:\\s*`, 'i'), '');

    // Sécurité absolue : Supprimer les salutations et présentations répétées si la conversation a déjà commencé
    if (!isFirstExchange) {
        cleaned = cleaned.replace(/^(bonjour|bonsoir|salut|bienvenue|coucou)[^.!?\n]*[.!?\n]\s*/i, '');
        cleaned = cleaned.replace(/^(je suis|c'est|ici)\s+[A-Za-zÀ-ÿ\s.'-]+[,.!?\n]\s*/i, '');
        cleaned = cleaned.replace(/^(bonjour|bonsoir|salut|bienvenue|coucou)[^.!?\n]*[.!?\n]\s*/i, '');
    }

    // Couper tout dialogue inventé pour le client par la suite
    if (cleaned.includes('\nMaster:')) cleaned = cleaned.split('\nMaster:')[0].trim();
    if (cleaned.includes(`\n${userName}:`)) cleaned = cleaned.split(`\n${userName}:`)[0].trim();
    if (cleaned.includes('\nClient:')) cleaned = cleaned.split('\nClient:')[0].trim();
    if (cleaned.includes('\nUser:')) cleaned = cleaned.split('\nUser:')[0].trim();
    if (cleaned.includes(`\n${currentMediumName}:`)) cleaned = cleaned.split(`\n${currentMediumName}:`)[0].trim();

    return cleaned;
  } catch (error: any) {
    console.error("Erreur getChatResponse Ollama:", error);
    return "";
  }
};

// --- ANALYSE DE FIN DE CHAT & MISE A JOUR CERVEAU IA ---
export interface AIAnalysisResult {
    age: string;
    summary: string;
    concerns: string;
    problem: string;
    facts: LearnedFact[];
    emotionalState: string;
    predictions: string[];
}

// ==================================================================================
// --- TRADUCTION & NORMALISATION FRANÇAISE DU CERVEAU IA ---
// ==================================================================================

export const EMOTIONAL_STATE_FR_MAP: Record<string, string> = {
    'optimistic': 'Optimiste',
    'anxious': 'Anxieux',
    'sad': 'Triste',
    'distressed': 'En détresse',
    'in distress': 'En détresse',
    'doubtful': 'Douteux',
    'peaceful': 'Apaisé',
    'calm': 'Calme & Posé',
    'neutral': 'Neutre',
    'vulnerable': 'Vulnérable',
    'determined': 'Déterminé',
    'playful': 'Espiègle',
    'hopeful': "Plein d'espoir",
    'confused': 'Indécis / Troublé',
    'angry': 'En colère / Blessé',
    'depressed': 'Abattu / Mélancolique',
    'inconnu': 'Non déterminé',
    'unknown': 'Non déterminé'
};

export const translateEmotionalState = (state: string): string => {
    if (!state) return 'Neutre';
    const lower = state.trim().toLowerCase();
    if (EMOTIONAL_STATE_FR_MAP[lower]) return EMOTIONAL_STATE_FR_MAP[lower];
    for (const [en, fr] of Object.entries(EMOTIONAL_STATE_FR_MAP)) {
        if (lower.includes(en)) return fr;
    }
    return state;
};

export const COMMON_ENGLISH_PHRASES_MAP: [RegExp, string][] = [
    [/Client\s+(.*?)\s+is seeking guidance about (?:his|her) sentimental life and experiencing a renewal in love\./gi, 'La consultante $1 recherche des conseils sur sa vie sentimentale et traverse un renouveau affectif.'],
    [/Client\s+(.*?)\s+is seeking guidance about (?:his|her) (.*?)\./gi, 'Le consultant $1 recherche une guidance concernant $2.'],
    [/Finding true love and happiness/gi, "Recherche du véritable amour et de l'épanouissement affectif"],
    [/Renewal of sentimental life/gi, "Renouveau de la vie sentimentale"],
    [/Renewal in love/gi, "Renouveau amoureux et affectif"],
    [/Seeking guidance about sentimental life/gi, "Recherche de conseils sur la vie sentimentale"],
    [/Seeking guidance/gi, "Recherche de guidance et d'éclairage"],
    [/Sentimental life/gi, "Vie sentimentale"],
    [/True love and happiness/gi, "Véritable amour et bonheur partagé"],
    [/Career change and development/gi, "Évolution de carrière et opportunités professionnelles"],
    [/Breakup with (?:his|her) partner/gi, "Rupture avec son partenaire"],
    [/Return of (?:his|her) ex/gi, "Retour de son ex-partenaire"],
    [/Financial difficulties/gi, "Difficultés financières et blocages matériels"],
    [/Family issues and conflicts/gi, "Conflits familiaux et tensions relationnelles"],
    [/Fear of abandonment/gi, "Peur de l'abandon et blessure affective"],
    [/Lack of self-confidence/gi, "Manque de confiance en soi et doutes"],
    [/Emotional blockages/gi, "Blocages émotionnels et nœuds karmiques"],
    [/Meeting someone new/gi, "Nouvelle rencontre amoureuse"],
    [/Decision making/gi, "Prise de décision importante"]
];

export const formatFrenchBrainText = (text: any): string => {
    if (!text) return '';
    let out = typeof text === 'string' ? text : ensureString(text);
    for (const [pattern, replacement] of COMMON_ENGLISH_PHRASES_MAP) {
        out = out.replace(pattern, replacement);
    }
    const lower = out.trim().toLowerCase();
    if (EMOTIONAL_STATE_FR_MAP[lower]) return EMOTIONAL_STATE_FR_MAP[lower];
    if (lower === 'none' || lower === 'not specified') return 'Non spécifié';
    if (lower === 'unknown') return 'Inconnu';
    return out;
};

export const analyzeConversation = async (history: ChatMessage[]): Promise<AIAnalysisResult> => {
    if (isAiGlobalKillSwitchActive()) {
        return { 
            age: "Inconnu", 
            summary: "Consultation enregistrée.", 
            concerns: "Non spécifié.",
            problem: "Non spécifié.",
            facts: [], 
            emotionalState: "Neutre", 
            predictions: [] 
        };
    }
    try {
        const transcript = history.map(m => `${m.role === 'user' ? 'Client' : 'Médium'}: ${m.text}`).join('\n');

        const prompt = `Tu es un analyste psychologique expert en voyance et relations humaines.
RÈGLE ABSOLUE ET INVIOLABLE : TOUTES LES VALEURS DU JSON (summary, concerns, problem, emotionalState, facts, predictions) DOIVENT ÊTRE RÉDIGÉES STRICTEMENT EN FRANÇAIS DE FRANCE.
AUCUN MOT EN ANGLAIS N'EST ACCEPTÉ. SI TU REÇOIS OU PENSES EN ANGLAIS, TRADUIS OBLIGATOIREMENT EN FRANÇAIS.

Analyse l'échange ci-dessous et produis la fiche synthétique du cerveau IA en respectant exactement cette structure JSON :
{
    "age": "Âge estimé ou mentionné en français (ex: 38 ans ou Inconnu)",
    "summary": "Synthèse et révélation de vie en 2 phrases complètes en français décrivant la situation, les désirs et événements vécus par le consultant",
    "concerns": "Sujet de consultation & préoccupations principales en français (ex: Rupture amoureuse, recherche d'un emploi, doutes affectifs)",
    "problem": "Nœud karmique, blocage ou défi fondamental identifié en français (ex: Recherche du véritable amour et de l'épanouissement affectif)",
    "emotionalState": "Profil émotionnel en français (choisir parmi: Optimiste, Anxieux, Triste, En détresse, Douteux, Apaisé, Espiègle, Vulnérable, Déterminé, Neutre)",
    "facts": [
        { "category": "amour", "fact": "Fait marquant ou confidence en français", "date": "${new Date().toLocaleDateString('fr-FR')}" }
    ],
    "predictions": ["Prédictions ou conseils clés formulés en français"]
}

Important : Les catégories autorisées pour facts sont : "amour", "travail", "vie_perso", "psychologie".
Toutes les descriptions doivent être en FRANÇAIS.

Conversation à analyser :
${transcript}`;

        const systemPrompt = `Tu es un analyste expert de voyance et psychologue francophone. Tu t'exprimes et rédiges TOUJOURS ET EXCLUSIVEMENT EN FRANÇAIS. L'anglais est STRICTEMENT INTERDIT dans les valeurs JSON. Réponds UNIQUEMENT en JSON valide strict, sans texte d'introduction ni de conclusion.`;

        const response = await callOllama(prompt, systemPrompt, 'json');
        
        if (response) {
            const result = cleanAndParseJSON(response);
            return {
                age: formatFrenchBrainText(result.age || "Inconnu"),
                summary: formatFrenchBrainText(result.summary || "Consultation effectuée."),
                concerns: formatFrenchBrainText(result.concerns || "Non spécifié."),
                problem: formatFrenchBrainText(result.problem || "Non spécifié."),
                facts: Array.isArray(result.facts) ? result.facts.map((f: any) => ({
                    category: (f.category || "amour").toLowerCase(),
                    fact: formatFrenchBrainText(f.fact || f.description || ""),
                    date: f.date || new Date().toLocaleDateString('fr-FR')
                })).filter((f: any) => f.fact && f.fact.trim().length > 0) : [],
                emotionalState: translateEmotionalState(result.emotionalState || "Neutre"),
                predictions: Array.isArray(result.predictions) ? result.predictions.map((p: any) => formatFrenchBrainText(p)).filter(Boolean) : []
            };
        }

        return { age: "Inconnu", summary: "Consultation enregistrée.", concerns: "Non spécifié.", problem: "Non spécifié.", facts: [], emotionalState: "Neutre", predictions: [] };

    } catch (error) {
        console.error("Erreur critique analyzeConversation:", error);
        return { 
            age: "Inconnu", 
            summary: "Discussion terminée.", 
            concerns: "Non spécifié.", 
            problem: "Non spécifié.", 
            facts: [], 
            emotionalState: "Neutre", 
            predictions: [] 
        };
    }
};

// --- SAFE STRING AND ARRAY HELPERS (PREVENTS REACT ERROR #31 ON OBJECT CHILDREN) ---
export const ensureString = (val: any, fallback: string = ""): string => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'string') return val.trim();
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (Array.isArray(val)) {
        return val.map(item => ensureString(item)).filter(Boolean).join('\n');
    }
    if (typeof val === 'object') {
        try {
            return Object.entries(val)
                .map(([k, v]) => `${k} : ${ensureString(v)}`)
                .filter(Boolean)
                .join('\n\n');
        } catch (e) {
            return fallback;
        }
    }
    return String(val);
};

export const ensureStringArray = (val: any, fallback: string[] = []): string[] => {
    if (!val) return fallback;
    if (Array.isArray(val)) {
        return val.map(item => ensureString(item)).filter(Boolean);
    }
    if (typeof val === 'object') {
        try {
            return Object.entries(val).map(([k, v]) => `${k} : ${ensureString(v)}`).filter(Boolean);
        } catch (e) {
            return fallback;
        }
    }
    if (typeof val === 'string') {
        return [val.trim()];
    }
    return fallback;
};

// --- AUTRES SERVICES (Horoscope, etc) ---

export const getHoroscope = async (sign: string): Promise<string> => {
    if (isAiGlobalKillSwitchActive()) {
        return getPreloadedHoroscope(sign);
    }
    try {
        const prompt = `Horoscope du jour très détaillé pour le signe ${sign}. Mystique, inspirant, structuré (Climat général, Amour, Travail, Conseil).`;
        const response = await callOllama(prompt, 'Tu es un astrologue expert. Génère des horoscopes détaillés, mystiques et bienveillants en français.');
        if (response) {
            return ensureString(response, getPreloadedHoroscope(sign));
        }
        return getPreloadedHoroscope(sign);
    } catch (error) {
        return getPreloadedHoroscope(sign);
    }
};

// --- MODIFICATION CRYSTAL BALL PROMPT (MYSTÈRE & CURIOSITÉ) ---
export const getCrystalBallPrediction = async (question: string): Promise<string> => {
    if (isAiGlobalKillSwitchActive()) {
        return getPreloadedCrystalBallPrediction(question);
    }
    try {
        const prompt = `Question du consultant : "${question}"

Consignes :
1. Ne réponds JAMAIS par Oui/Non.
2. Sois mystérieux, évoque des "ombres", des "silhouettes", des "blocages énergétiques".
3. Termine par une phrase incomplète ou une mise en garde qui incite à en savoir plus.
4. Ton but est qu'ils aient envie de consulter pour avoir la suite.
5. Reste bienveillant mais énigmatique.
6. Max 2 phrases courtes.`;
        const systemPrompt = 'Tu es un Oracle de Cristal antique. Ta mission : Provoquer une curiosité intense (Effet Barnum & Cliffhanger). Réponds en français.';
        const response = await callOllama(prompt, systemPrompt);
        if (response) {
            return ensureString(response, getPreloadedCrystalBallPrediction(question));
        }
        return getPreloadedCrystalBallPrediction(question);
    } catch (error) {
        console.warn("CrystalBall Ollama Error, using preloaded answers database:", error);
        return getPreloadedCrystalBallPrediction(question);
    }
};

export interface CompatibilityResult {
    score: number;
    title: string;
    emotional: string;
    communication: string;
    intimacy: string;
    challenges: string[];
    advice: string;
}

export const getLoveCompatibility = async (sign1: string, sign2: string): Promise<CompatibilityResult> => {
    if (isAiGlobalKillSwitchActive()) {
        return getPreloadedLoveCompatibility(sign1, sign2);
    }
    try {
        const prompt = `Compatibilité amoureuse ${sign1} et ${sign2}.
Génère un JSON STRICT avec:
{
    "score": 88,
    "title": "${sign1} & ${sign2} : Harmonie Astrale",
    "emotional": "Texte décrivant la connexion émotionnelle",
    "communication": "Texte décrivant la communication",
    "intimacy": "Texte décrivant la passion et l'alchimie",
    "challenges": ["Défi 1", "Défi 2"],
    "advice": "Conseil des astres"
}`;
        const response = await callOllama(prompt, 'Tu es un astrologue expert en compatibilité amoureuse. Réponds UNIQUEMENT en JSON valide strict avec des valeurs textuelles simples.', 'json');
        if (response) {
            const parsed = cleanAndParseJSON(response);
            if (parsed && typeof parsed === 'object') {
                const score = typeof parsed.score === 'number' ? Math.min(100, Math.max(0, parsed.score)) : (parseInt(parsed.score, 10) || 88);
                const title = ensureString(parsed.title, `${sign1} & ${sign2} : Harmonie Céleste`);
                const emotional = ensureString(parsed.emotional);
                const communication = ensureString(parsed.communication);
                const intimacy = ensureString(parsed.intimacy);
                const challenges = ensureStringArray(parsed.challenges, [
                    "Respecter les rythmes et le besoin d'espace de chacun",
                    "Privilégier le dialogue direct pour dissiper les non-dits"
                ]);
                const advice = ensureString(parsed.advice);

                if (emotional && communication && intimacy && advice) {
                    return {
                        score,
                        title,
                        emotional,
                        communication,
                        intimacy,
                        challenges,
                        advice
                    };
                }
            }
        }
        return getPreloadedLoveCompatibility(sign1, sign2);
    } catch (error) {
        console.warn("LoveCompatibility Ollama Error, using preloaded compatibility database:", error);
        return getPreloadedLoveCompatibility(sign1, sign2);
    }
};

export const getNumerologyReading = async (number: number) => {
    if (isAiGlobalKillSwitchActive()) {
        return getPreloadedNumerologyReading(number);
    }
    try {
        const prompt = `Numérologie Chemin de Vie ${number}. Génère un JSON STRICT avec: description, qualities (array de strings), challenges (array de strings), love, work, advice.`;
        const response = await callOllama(prompt, 'Tu es un numérologue expert. Réponds UNIQUEMENT en JSON valide, sans texte avant ni après.', 'json');
        if (response) {
            const parsed = cleanAndParseJSON(response);
            if (parsed && typeof parsed === 'object') {
                const description = ensureString(parsed.description);
                const qualities = ensureStringArray(parsed.qualities, ["Intuition", "Détermination"]);
                const challenges = ensureStringArray(parsed.challenges, ["Patience", "Lâcher-prise"]);
                const love = ensureString(parsed.love);
                const work = ensureString(parsed.work);
                const advice = ensureString(parsed.advice);

                if (description && love && work && advice) {
                    return { description, qualities, challenges, love, work, advice };
                }
            }
        }
        return getPreloadedNumerologyReading(number);
    } catch (error) {
        console.warn("Numerology Ollama Error, using preloaded numerology database:", error);
        return getPreloadedNumerologyReading(number);
    }
};

export type { DreamAnalysisResult };

export const analyzeDream = async (dreamText: string): Promise<DreamAnalysisResult> => {
    if (isAiGlobalKillSwitchActive()) {
        return getPreloadedDreamAnalysis(dreamText);
    }
    try {
        const prompt = `Interprétation de rêve: "${dreamText}". Génère un JSON STRICT avec: title, analysis, symbols (array d'objets {name, meaning}), advice, warning, prediction.`;
        const response = await callOllama(prompt, 'Tu es un oneirologue expert en interprétation des rêves. Réponds UNIQUEMENT en JSON valide, sans texte avant ni après.', 'json');
        if (response) {
            const parsed = cleanAndParseJSON(response);
            if (parsed && typeof parsed === 'object') {
                const title = ensureString(parsed.title, "Message de votre inconscient");
                const analysis = ensureString(parsed.analysis);
                const symbols = Array.isArray(parsed.symbols)
                    ? parsed.symbols.map((s: any) => ({
                        name: ensureString(s?.name || s?.symbole || "Symbole"),
                        meaning: ensureString(s?.meaning || s?.signification || s)
                    })).filter((s: any) => s.name && s.meaning)
                    : [];
                const advice = ensureString(parsed.advice);
                const warning = ensureString(parsed.warning);
                const prediction = ensureString(parsed.prediction);

                if (title && analysis) {
                    return { title, analysis, symbols, advice, warning, prediction };
                }
            }
        }
        return getPreloadedDreamAnalysis(dreamText);
    } catch (error) {
        return getPreloadedDreamAnalysis(dreamText);
    }
};

// --- TAROT READING (MULTI-SPREAD ENGINE) ---
import { 
    TarotReadingResult, 
    TarotCardPositionReading, 
    generateMultiSpreadTarotReading, 
    getRandomOrMatchingTarotReading,
    TAROT_SPREADS
} from '../data/tarotPreloadedReadings';
export { type TarotReadingResult, type TarotCardPositionReading } from '../data/tarotPreloadedReadings';

export const getTarotReading = async (question: string, cards: string[], spreadId?: string): Promise<TarotReadingResult> => {
    const determinedSpread = spreadId || (cards.length === 1 ? 'single' : cards.length === 5 ? 'cross' : cards.length === 7 ? 'horseshoe' : cards.length >= 12 ? 'astrological' : 'three');
    
    if (isAiGlobalKillSwitchActive()) {
        return generateMultiSpreadTarotReading(determinedSpread, question, cards);
    }
    try {
        const config = TAROT_SPREADS.find(s => s.id === determinedSpread) || TAROT_SPREADS[1];
        const cardsDesc = cards.map((c, i) => `${i + 1}. ${config.positionNames[i]?.title || `Position ${i + 1}`} : ${c}`).join('\n');
        
        const prompt = `Tirage de Tarot de Marseille (${config.name}).
Question du consultant : "${question || "Guidance générale"}"
Cartes tirées :
${cardsDesc}

Génère un JSON STRICT avec :
{
    "advice": "Conseil ésotérique bienveillant (texte simple)",
    "synthesis": "Synthèse globale d'avenir (texte simple)",
    "positions": [
        ${cards.map((c, i) => `{"index": ${i}, "positionTitle": "${config.positionNames[i]?.title || `Position ${i+1}`}", "cardName": "${c}", "text": "Interprétation profonde de cette carte dans cette position spécifique"}`).join(',\n        ')}
    ]
}`;
        const response = await callOllama(prompt, 'Tu es une grande tarologue experte. Interprète ce tirage avec mystère, précision et bienveillance. Réponds UNIQUEMENT en JSON valide, sans texte avant ni après.', 'json');
        if (response) {
            const parsed = cleanAndParseJSON(response);
            if (parsed && typeof parsed === 'object') {
                const advice = ensureString(parsed.advice);
                const synthesis = ensureString(parsed.synthesis);
                const rawPositions = Array.isArray(parsed.positions) ? parsed.positions : [];
                
                const positions: TarotCardPositionReading[] = cards.map((c, i) => {
                    const found = rawPositions.find((p: any) => p.index === i || p.cardName === c);
                    const posInfo = config.positionNames[i] || { title: `Position ${i + 1}`, role: 'Rôle' };
                    return {
                        index: i,
                        positionTitle: posInfo.title,
                        positionRole: posInfo.role,
                        cardName: c,
                        symbol: 'énergie dominante',
                        text: (found && typeof found.text === 'string' && found.text.trim()) ? found.text : `Interprétation de ${c} pour ${posInfo.title}`
                    };
                });

                if (advice && positions.length > 0) {
                    return {
                        past: positions[0]?.text || '',
                        present: positions[1]?.text || positions[0]?.text || '',
                        future: positions[2]?.text || positions[positions.length - 1]?.text || '',
                        advice,
                        synthesis: synthesis || `Synthèse des ${cards.length} arcanes`,
                        spreadId: determinedSpread,
                        spreadName: config.name,
                        positions
                    };
                }
            }
        }
        return generateMultiSpreadTarotReading(determinedSpread, question, cards);
    } catch (error) {
        console.warn("Tarot Ollama Error, using preloaded multi-spread database:", error);
        return generateMultiSpreadTarotReading(determinedSpread, question, cards);
    }
};
